import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Clock, Check, Menu as MenuIcon, QrCode, ShoppingCart, CalendarClock, Bike, MessageSquare, Eye, EyeOff, Hash, CheckCircle2, Flame, Sparkles, Building2, LayoutGrid, Layers, Filter, ChevronDown } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { useOrders, Order, OrderItem, OrderMethod, methodConfig, stationForItem } from './OrdersContext';
import { useBranches, KitchenStation } from './BranchesContext';
import { useStock } from './StockContext';
import { useBranchMenu } from './MenusContext';
import { useOutlets } from './OutletsContext';
import { StationIconBadge } from './BranchStationsSection';
const methodIcon = (m: OrderMethod) => {
  if (m === 'table') return QrCode;
  if (m === 'takeout') return ShoppingCart;
  if (m === 'delivery') return Bike;
  return CalendarClock;
};
type UrgencyLevel = 'fresh' | 'warm' | 'overdue';
function urgencyOf(placedAt: number): {
  level: UrgencyLevel;
  mins: number;
  secs: number;
} {
  const totalSecs = Math.max(0, Math.floor((Date.now() - placedAt) / 1000));
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  let level: UrgencyLevel = 'fresh';
  if (mins >= 15) level = 'overdue';else if (mins >= 5) level = 'warm';
  return {
    level,
    mins,
    secs
  };
}
const urgencyStyle: Record<UrgencyLevel, {
  border: string;
  bar: string;
  chip: string;
  chipText: string;
}> = {
  fresh: {
    border: 'border-emerald-300',
    bar: 'bg-emerald-500',
    chip: 'bg-emerald-100',
    chipText: 'text-emerald-800'
  },
  warm: {
    border: 'border-amber-300',
    bar: 'bg-amber-500',
    chip: 'bg-amber-100',
    chipText: 'text-amber-800'
  },
  overdue: {
    border: 'border-red-400',
    bar: 'bg-red-500',
    chip: 'bg-red-100',
    chipText: 'text-red-800'
  }
};
const stationDotColor: Record<string, string> = {
  orange: 'bg-orange-500',
  emerald: 'bg-emerald-500',
  blue: 'bg-blue-500',
  pink: 'bg-pink-500',
  amber: 'bg-amber-500',
  purple: 'bg-purple-500',
  rose: 'bg-rose-500',
  indigo: 'bg-indigo-500'
};
const stationChipBg: Record<string, string> = {
  orange: 'bg-orange-100 text-orange-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  blue: 'bg-blue-100 text-blue-700',
  pink: 'bg-pink-100 text-pink-700',
  amber: 'bg-amber-100 text-amber-700',
  purple: 'bg-purple-100 text-purple-700',
  rose: 'bg-rose-100 text-rose-700',
  indigo: 'bg-indigo-100 text-indigo-700'
};
type ViewMode = 'combined' | 'station' | 'expediter';
export function KitchenPage() {
  const {
    branchId,
    branches,
    setBranchId
  } = useBranches();
  const {
    orders,
    setStatus,
    setItemReady,
    setAllItemsReady
  } = useOrders();
  const {
    outOfStock,
    isOutOfStock,
    toggleStock
  } = useStock();
  const {
    getOutletsForBranch
  } = useOutlets();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedStationId, setSelectedStationId] = useState<string>('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showReady, setShowReady] = useState(true);
  const [outletFilter, setOutletFilter] = useState<string>('all');
  const branch = branches.find(b => b.id === branchId) || branches[0];
  const branchMenu = useBranchMenu(branchId);
  const branchOutlets = getOutletsForBranch(branchId);
  const stations = useMemo(() => {
    return [{
      id: 'all',
      name: 'Expediter (All)',
      icon: 'layers'
    }, ...(branch?.stations || [])];
  }, [branch]);
  const stationById: Record<string, KitchenStation | undefined> = {};
  stations.forEach(s => stationById[s.id] = s as KitchenStation);
  // Force a re-render every second so the live timers tick.
  const [, force] = useState(0);
  const [completingId, setCompletingId] = useState<string | null>(null);
  // View mode (defaults: combined if single-station, expediter if multi-station)
  const [viewMode, setViewMode] = useState<ViewMode>('expediter');
  const [stationId, setStationId] = useState<string>(stations[0]?.id || 'food');
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  // Reset view when branch changes
  useEffect(() => {
    if (stations.length <= 1) setViewMode('combined');else if (viewMode === 'combined') setViewMode('expediter');
    if (!stations.find(s => s.id === stationId)) {
      setStationId(stations[0]?.id || 'food');
    }
    setOutletFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);
  // Tick the clock every second for live timers
  useEffect(() => {
    const id = setInterval(() => force(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const preparingOrders = useMemo(() => orders.filter(o => o.status === 'preparing' && (outletFilter === 'all' || o.outletId === outletFilter)).sort((a, b) => a.placedAt - b.placedAt), [orders, outletFilter]);
  const readyOrders = useMemo(() => orders.filter(o => o.status === 'ready' && (outletFilter === 'all' || o.outletId === outletFilter)).sort((a, b) => b.placedAt - a.placedAt), [orders, outletFilter]);
  // In station mode, only show orders that have at least one item for this station
  const filteredPreparing = useMemo(() => {
    if (viewMode !== 'station') return preparingOrders;
    return preparingOrders.filter(o => o.items.some(it => stationForItem(it) === stationId));
  }, [preparingOrders, viewMode, stationId]);
  const overdueCount = filteredPreparing.filter(o => urgencyOf(o.placedAt).level === 'overdue').length;
  // Auto-complete: when ALL items (across all stations) are ready, transition to "ready"
  useEffect(() => {
    preparingOrders.forEach(o => {
      const allDone = o.items.length > 0 && o.items.every(it => it.ready);
      if (allDone && completingId !== o.id) {
        setCompletingId(o.id);
        setTimeout(() => {
          setStatus(o.id, 'ready');
          setCompletingId(cur => cur === o.id ? null : cur);
        }, 900);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preparingOrders]);
  const handleToggleItem = (orderId: string, itemId: string, ready: boolean) => {
    setItemReady(orderId, itemId, ready);
  };
  const handleMarkAllReady = (orderId: string) => {
    setAllItemsReady(orderId);
  };
  const handleMarkStationReady = (orderId: string, stId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    order.items.forEach(it => {
      if (stationForItem(it) === stId && !it.ready) {
        setItemReady(orderId, it.id, true);
      }
    });
  };
  const isMultiStation = stations.length > 1;
  // Gather all unique item names from branch menu and recent orders to show in the stock modal
  const allKnownItemNames = useMemo(() => {
    const names = new Set<string>();
    branchMenu.forEach(i => names.add(i.name));
    orders.forEach(o => o.items.forEach(i => names.add(i.name)));
    return Array.from(names).sort();
  }, [orders, branchMenu]);
  return <div className="flex h-screen w-full bg-slate-900 overflow-hidden font-sans flex-col md:flex-row" data-id="element-2213">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30 border-b border-slate-700" data-id="element-2214">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-2215">
          <MenuIcon size={24} data-id="element-2216" />
        </button>
        <span className="font-semibold flex items-center gap-2" data-id="element-2217">
          <ChefHat size={18} className="text-orange-400" data-id="element-2218" />
          Kitchen
        </span>
        <div className="w-6" data-id="element-2219" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-2220" />

      <main className="flex-1 flex flex-col h-full overflow-hidden bg-slate-900" data-id="element-2221">
        {/* Header bar */}
        <div className="px-4 md:px-6 py-4 border-b border-slate-700/60 bg-slate-900 flex-shrink-0" data-id="element-2222">
          <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-2223">
            <div className="flex items-center gap-3 min-w-0" data-id="element-2224">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center flex-shrink-0" data-id="element-2225">
                <ChefHat size={20} data-id="element-2226" />
              </div>
              <div className="min-w-0" data-id="element-2227">
                <div className="text-[11px] font-semibold text-orange-400 uppercase tracking-wider" data-id="element-2228">
                  Kitchen Display
                </div>
                <h1 className="text-xl font-bold text-white truncate" data-id="element-2229">
                  {viewMode === 'station' && stationById[stationId] ? stationById[stationId].name : viewMode === 'expediter' ? 'Expediter overview' : 'Active tickets'}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-2230">
              {/* Branch picker */}
              <div className="relative" data-id="element-2231">
                <Building2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-2232" />
                <select value={branchId} onChange={e => setBranchId(e.target.value)} className="appearance-none pl-7 pr-7 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-xs font-semibold focus:outline-none focus:border-slate-500 cursor-pointer hover:bg-slate-700 transition-colors" data-id="element-2233">
                  {branches.map(b => <option key={b.id} value={b.id} data-id="element-2234">
                      {b.name}
                      {!b.active ? ' (inactive)' : ''}
                    </option>)}
                </select>
                <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-2235" />
              </div>

              {/* Stats */}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-xs" data-id="element-2236">
                <Flame size={12} className="text-orange-400" data-id="element-2237" />
                <span className="font-bold tabular-nums" data-id="element-2238">
                  {filteredPreparing.length}
                </span>
                <span className="text-slate-400" data-id="element-2239">in queue</span>
              </span>
              {overdueCount > 0 && <motion.span initial={{
              scale: 0.9,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 text-xs" data-id="element-2240">
                  <span className="relative flex h-1.5 w-1.5" data-id="element-2241">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" data-id="element-2242" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500" data-id="element-2243" />
                  </span>
                  <span className="font-bold tabular-nums" data-id="element-2244">{overdueCount}</span>
                  <span data-id="element-2245">overdue</span>
                </motion.span>}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-xs" data-id="element-2246">
                <CheckCircle2 size={12} className="text-emerald-400" data-id="element-2247" />
                <span className="font-bold tabular-nums" data-id="element-2248">
                  {readyOrders.length}
                </span>
                <span className="text-slate-400" data-id="element-2249">ready</span>
              </span>

              {/* Toggle ready */}
              <button onClick={() => setShowReady(v => !v)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 transition-colors text-xs font-medium" data-id="element-2250">
                {showReady ? <EyeOff size={12} data-id="element-2251" /> : <Eye size={12} data-id="element-2252" />}
                {showReady ? 'Hide ready' : 'Show ready'}
              </button>
            </div>
          </div>

          {/* View mode + station selector */}
          {isMultiStation && <div className="mt-3 flex items-center gap-2 flex-wrap" data-id="element-2253">
              <div className="inline-flex items-center bg-slate-800 border border-slate-700 rounded-lg p-0.5" data-id="element-2254">
                <ModeButton active={viewMode === 'expediter'} onClick={() => setViewMode('expediter')} icon={<Layers size={12} data-id="element-2256" />} label="Expediter" data-id="element-2255" />
                <ModeButton active={viewMode === 'station'} onClick={() => setViewMode('station')} icon={<Filter size={12} data-id="element-2258" />} label="Station view" data-id="element-2257" />
                <ModeButton active={viewMode === 'combined'} onClick={() => setViewMode('combined')} icon={<LayoutGrid size={12} data-id="element-2260" />} label="All items" data-id="element-2259" />
              </div>

              {viewMode === 'station' && <motion.div initial={{
            opacity: 0,
            x: -6
          }} animate={{
            opacity: 1,
            x: 0
          }} className="inline-flex items-center gap-1 flex-wrap" data-id="element-2261">
                  {stations.map(s => {
              const isActive = s.id === stationId;
              return <button key={s.id} onClick={() => setStationId(s.id)} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${isActive ? 'bg-white text-slate-900' : 'bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700'}`} data-id="element-2262">
                        <StationIconBadge icon={s.icon} color={s.color} size={11} data-id="element-2263" />
                        {s.name}
                      </button>;
            })}
                </motion.div>}

              {branchOutlets.length > 0 && <div className="ml-auto flex items-center gap-2" data-id="element-2264">
                  <div className="text-xs font-medium text-slate-400" data-id="element-2265">
                    Outlet:
                  </div>
                  <div className="relative" data-id="element-2266">
                    <select value={outletFilter} onChange={e => setOutletFilter(e.target.value)} className="appearance-none bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg pl-3 pr-8 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-500" data-id="element-2267">
                      <option value="all" data-id="element-2268">All Outlets</option>
                      {branchOutlets.map(o => <option key={o.id} value={o.id} data-id="element-2269">
                          {o.name}
                        </option>)}
                    </select>
                    <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-2270" />
                  </div>
                </div>}
            </div>}
        </div>

        {/* Tickets grid */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6" data-id="element-2271">
          {filteredPreparing.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-2272">
              <AnimatePresence mode="popLayout" data-id="element-2273">
                {filteredPreparing.map(order => {
              if (viewMode === 'station') {
                return <StationTicket key={order.id} order={order} stationId={stationId} station={stationById[stationId]} onToggleItem={(itemId, ready) => handleToggleItem(order.id, itemId, ready)} onMarkStationReady={() => handleMarkStationReady(order.id, stationId)} data-id="element-2274" />;
              }
              if (viewMode === 'expediter') {
                return <ExpediterTicket key={order.id} order={order} stations={stations} onToggleItem={(itemId, ready) => handleToggleItem(order.id, itemId, ready)} onMarkStationReady={stId => handleMarkStationReady(order.id, stId)} onMarkAllReady={() => handleMarkAllReady(order.id)} isCompleting={completingId === order.id} data-id="element-2275" />;
              }
              return <KitchenTicket key={order.id} order={order} onToggleItem={(itemId, ready) => handleToggleItem(order.id, itemId, ready)} onMarkAllReady={() => handleMarkAllReady(order.id)} isCompleting={completingId === order.id} data-id="element-2276" />;
            })}
              </AnimatePresence>
            </div> : <EmptyState viewMode={viewMode} stationName={stationById[stationId]?.name} data-id="element-2277" />}

          {/* Ready section */}
          {showReady && readyOrders.length > 0 && <div className="mt-10" data-id="element-2278">
              <div className="flex items-center gap-3 mb-4" data-id="element-2279">
                <div className="flex-1 h-px bg-slate-700" data-id="element-2280" />
                <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-2281">
                  <CheckCircle2 size={13} data-id="element-2282" />
                  Ready for pickup ({readyOrders.length})
                </span>
                <div className="flex-1 h-px bg-slate-700" data-id="element-2283" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-2284">
                {readyOrders.map(order => <ReadyTicket key={order.id} order={order} data-id="element-2285" />)}
              </div>
            </div>}
        </div>
      </main>

      {/* Stock Modal */}
      {isStockModalOpen && <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-2286">
          <div className="bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full flex flex-col max-h-[85vh] border border-slate-700" data-id="element-2287">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between" data-id="element-2288">
              <div className="flex items-center gap-3" data-id="element-2289">
                <div className="w-10 h-10 rounded-xl bg-rose-500/20 text-rose-400 flex items-center justify-center" data-id="element-2290">
                  <PackageX size={20} data-id="element-2291" />
                </div>
                <div data-id="element-2292">
                  <h2 className="text-lg font-bold text-white" data-id="element-2293">Item Stock</h2>
                  <p className="text-xs text-slate-400" data-id="element-2294">
                    Mark items out of stock to hide them from menus.
                  </p>
                </div>
              </div>
              <button onClick={() => setIsStockModalOpen(false)} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors" data-id="element-2295">
                <X size={20} data-id="element-2296" />
              </button>
            </div>
            <div className="p-2 overflow-y-auto flex-1" data-id="element-2297">
              {allKnownItemNames.map(name => {
            const isOut = outOfStock.includes(name.toLowerCase());
            return <button key={name} onClick={() => toggleStock(name)} className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${isOut ? 'bg-rose-500/10 border border-rose-500/20' : 'hover:bg-slate-700/50 border border-transparent'}`} data-id="element-2298">
                    <span className={`font-semibold ${isOut ? 'text-rose-400' : 'text-slate-200'}`} data-id="element-2299">
                      {name}
                    </span>
                    <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isOut ? 'bg-rose-500' : 'bg-slate-600'}`} data-id="element-2300">
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${isOut ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-2301" />
                    </div>
                  </button>;
          })}
            </div>
          </div>
        </div>}
    </div>;
}
function ModeButton({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return <button onClick={onClick} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-300 hover:text-white'}`} data-id="element-2302">
      {icon}
      {label}
    </button>;
}
function TicketHeader({
  order
}: {
  order: Order;
}) {
  const {
    mins,
    secs,
    level
  } = urgencyOf(order.placedAt);
  const styles = urgencyStyle[level];
  const MethodIcon = methodIcon(order.method);
  const mc = methodConfig[order.method];
  return <div className="px-4 pt-3 pb-2 border-b border-gray-100" data-id="element-2303">
      <div className="flex items-start justify-between gap-2 mb-2" data-id="element-2304">
        <div className="flex items-center gap-2" data-id="element-2305">
          <span className="font-mono text-sm font-bold text-gray-900" data-id="element-2306">
            {order.id}
          </span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${mc.color} ${mc.bg}`} data-id="element-2307">
            <MethodIcon size={10} data-id="element-2308" />
            {order.tableNumber ? `T${order.tableNumber}` : mc.short}
          </span>
          {order.isOpenTab && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border border-blue-200 bg-blue-50 text-blue-700" data-id="element-2309">
              Open Tab
            </span>}
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold tabular-nums ${styles.chip} ${styles.chipText}`} data-id="element-2310">
          <Clock size={11} data-id="element-2311" />
          {mins}:{secs.toString().padStart(2, '0')}
        </div>
      </div>
      <div className="text-xs text-gray-500 truncate" data-id="element-2312">{order.customerName}</div>
    </div>;
}
function ItemRow({
  item,
  onToggle,
  stationChip
}: {
  item: OrderItem;
  onToggle: (ready: boolean) => void;
  stationChip?: {
    name: string;
    color: string;
  };
}) {
  return <button key={item.id} type="button" onClick={() => onToggle(!item.ready)} className={`w-full text-left py-2 px-2 rounded-lg transition-colors flex items-start gap-3 ${item.ready ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-gray-50'}`} data-id="element-2313">
      <div className="flex-shrink-0 mt-0.5" data-id="element-2314">
        {item.ready ? <div className="w-5 h-5 rounded-md bg-emerald-500 border-2 border-emerald-500 flex items-center justify-center" data-id="element-2315">
            <Check size={13} strokeWidth={3} className="text-white" data-id="element-2316" />
          </div> : <div className="w-5 h-5 rounded-md border-2 border-gray-300 bg-white" data-id="element-2317" />}
      </div>
      <div className="flex-1 min-w-0" data-id="element-2318">
        <div className="flex items-baseline gap-2 flex-wrap" data-id="element-2319">
          <span className={`text-base font-bold tabular-nums leading-none ${item.ready ? 'text-gray-400' : 'text-orange-600'}`} data-id="element-2320">
            ×{item.quantity}
          </span>
          <span className={`font-bold text-gray-900 leading-tight flex-1 ${item.ready ? 'line-through text-gray-400' : ''}`} data-id="element-2321">
            {item.name}
          </span>
          {item.variation && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 uppercase ${item.ready ? 'line-through text-gray-400' : ''}`} data-id="element-2322">
              {item.variation}
            </span>}
          {stationChip && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${stationChipBg[stationChip.color] || 'bg-gray-100 text-gray-700'} ${item.ready ? 'opacity-50' : ''}`} data-id="element-2323">
              <span className={`w-1.5 h-1.5 rounded-full ${stationDotColor[stationChip.color] || 'bg-gray-400'}`} data-id="element-2324" />
              {stationChip.name}
            </span>}
        </div>
        {item.modifiers && item.modifiers.length > 0 && <ul className="mt-1 ml-7 space-y-0.5" data-id="element-2325">
            {item.modifiers.map((m, i) => <li key={i} className={`text-[12px] text-gray-600 flex items-baseline gap-1 ${item.ready ? 'text-gray-400 line-through' : ''}`} data-id="element-2326">
                <span className="text-emerald-500 font-bold" data-id="element-2327">+</span>
                <span className="font-medium" data-id="element-2328">{m.name}</span>
              </li>)}
          </ul>}
        {item.notes && <div className="mt-1 ml-7 text-[12px] italic text-gray-500" data-id="element-2329">
            "{item.notes}"
          </div>}
      </div>
    </button>;
}
// === Combined ticket (single-station / all items mode) ===
function KitchenTicket({
  order,
  onToggleItem,
  onMarkAllReady,
  isCompleting
}: {
  order: Order;
  onToggleItem: (itemId: string, ready: boolean) => void;
  onMarkAllReady: () => void;
  isCompleting: boolean;
}) {
  const {
    getOutletsForBranch
  } = useOutlets();
  const branchOutlets = getOutletsForBranch(order.branchId || '');
  const outlet = order.outletId ? branchOutlets.find(o => o.id === order.outletId) : null;
  const {
    level
  } = urgencyOf(order.placedAt);
  const styles = urgencyStyle[level];
  const itemCount = order.items.reduce((s, i) => s + i.quantity, 0);
  const readyItems = order.items.filter(i => i.ready).length;
  const totalItems = order.items.length;
  const allReady = readyItems === totalItems && totalItems > 0;
  const progressPct = totalItems > 0 ? readyItems / totalItems * 100 : 0;
  return <motion.div layout initial={{
    opacity: 0,
    y: 12,
    scale: 0.96
  }} animate={{
    opacity: 1,
    y: 0,
    scale: isCompleting ? 1.02 : 1
  }} exit={{
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2
    }
  }} transition={{
    duration: 0.25
  }} className={`relative bg-white rounded-xl border-2 overflow-hidden shadow-lg ${styles.border} ${level === 'overdue' ? 'animate-[pulse_2s_ease-in-out_infinite]' : ''}`} data-id="element-2330">
      <div className={`h-1.5 ${styles.bar}`} data-id="element-2331" />
      <TicketHeader order={order} data-id="element-2332" />
      <div className="px-4 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wide" data-id="element-2333">
        {itemCount} item{itemCount === 1 ? '' : 's'}
      </div>
      {order.method === 'delivery' && order.deliveryAddress && <div className="bg-blue-50 border-y border-blue-200 px-4 py-2 flex gap-2 items-start" data-id="element-2334">
          <Bike size={13} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2335" />
          <p className="text-xs font-semibold text-blue-900 leading-snug" data-id="element-2336">
            {order.deliveryAddress}
          </p>
        </div>}
      {order.customerNotes && <div className="bg-amber-50 border-y border-amber-200 px-4 py-2 flex gap-2 items-start" data-id="element-2337">
          <MessageSquare size={13} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2338" />
          <p className="text-xs font-semibold text-amber-900 leading-snug" data-id="element-2339">
            {order.customerNotes}
          </p>
        </div>}
      <div className="px-4 pt-3 pb-1" data-id="element-2340">
        <div className="flex items-center justify-between mb-1.5" data-id="element-2341">
          <span className={`text-[10px] font-bold tracking-wider uppercase ${allReady ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-2342">
            {readyItems} of {totalItems} ready
          </span>
          {allReady && <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-2343">
              ALL DONE
            </span>}
        </div>
        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-2344">
          <motion.div className="h-full rounded-full bg-emerald-500" initial={false} animate={{
          width: `${progressPct}%`
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} data-id="element-2345" />
        </div>
      </div>
      <div className="px-4 py-3 space-y-2.5" data-id="element-2346">
        {order.items.map(item => <ItemRow key={item.id} item={item} onToggle={ready => onToggleItem(item.id, ready)} data-id="element-2347" />)}
      </div>
      <button onClick={onMarkAllReady} disabled={allReady} className={allReady ? 'w-full py-3.5 text-white font-bold text-sm border-t bg-emerald-600 border-emerald-700 cursor-default animate-pulse flex items-center justify-center gap-2' : 'w-full py-3.5 text-white font-bold text-sm border-t bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 border-emerald-600 transition-colors flex items-center justify-center gap-2'} data-id="element-2348">
        {allReady ? <>
            <Sparkles size={16} strokeWidth={3} data-id="element-2349" />
            Sending to ready queue...
          </> : <>
            <Check size={16} strokeWidth={3} data-id="element-2350" />
            {readyItems > 0 ? 'Mark remaining ready' : 'Mark all ready'}
          </>}
      </button>
    </motion.div>;
}
// === Station-filtered ticket (single station's items only) ===
function StationTicket({
  order,
  stationId,
  station,
  onToggleItem,
  onMarkStationReady
}: {
  order: Order;
  stationId: string;
  station?: KitchenStation;
  onToggleItem: (itemId: string, ready: boolean) => void;
  onMarkStationReady: () => void;
}) {
  const {
    getOutletsForBranch
  } = useOutlets();
  const branchOutlets = getOutletsForBranch(order.branchId || '');
  const outlet = order.outletId ? branchOutlets.find(o => o.id === order.outletId) : null;
  const {
    level
  } = urgencyOf(order.placedAt);
  const styles = urgencyStyle[level];
  const myItems = order.items.filter(it => stationForItem(it) === stationId);
  const readyMine = myItems.filter(i => i.ready).length;
  const totalMine = myItems.length;
  const allMineReady = readyMine === totalMine && totalMine > 0;
  const otherStationsReady = order.items.filter(it => stationForItem(it) !== stationId).every(it => it.ready);
  const orderFullyReady = allMineReady && otherStationsReady && order.items.length > 0;
  return <motion.div layout initial={{
    opacity: 0,
    y: 12,
    scale: 0.96
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2
    }
  }} transition={{
    duration: 0.25
  }} className={`relative bg-white rounded-xl border-2 overflow-hidden shadow-lg ${styles.border}`} data-id="element-2351">
      <div className={`h-1.5 ${styles.bar}`} data-id="element-2352" />
      <TicketHeader order={order} data-id="element-2353" />
      {station && <div className="px-4 pt-2 pb-1 flex items-center justify-between" data-id="element-2354">
          <div className="flex items-center gap-1.5" data-id="element-2355">
            <StationIconBadge icon={station.icon} color={station.color} size={11} data-id="element-2356" />
            <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wide" data-id="element-2357">
              {station.name} · {totalMine} item{totalMine === 1 ? '' : 's'}
            </span>
          </div>
          {!orderFullyReady && <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide" data-id="element-2358">
              {order.items.length - totalMine} elsewhere
            </span>}
        </div>}
      {order.customerNotes && <div className="bg-amber-50 border-y border-amber-200 px-4 py-2 flex gap-2 items-start" data-id="element-2359">
          <MessageSquare size={13} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2360" />
          <p className="text-xs font-semibold text-amber-900 leading-snug" data-id="element-2361">
            {order.customerNotes}
          </p>
        </div>}
      <div className="px-4 py-3 space-y-2.5" data-id="element-2362">
        {myItems.map(item => <ItemRow key={item.id} item={item} onToggle={ready => onToggleItem(item.id, ready)} data-id="element-2363" />)}
      </div>
      <button onClick={onMarkStationReady} disabled={allMineReady} className={allMineReady ? 'w-full py-3.5 text-white font-bold text-sm border-t bg-emerald-600 border-emerald-700 cursor-default flex items-center justify-center gap-2' : 'w-full py-3.5 text-white font-bold text-sm border-t bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 border-emerald-600 transition-colors flex items-center justify-center gap-2'} data-id="element-2364">
        {allMineReady ? <>
            <Check size={16} strokeWidth={3} data-id="element-2365" />
            Station done
          </> : <>
            <Check size={16} strokeWidth={3} data-id="element-2366" />
            Mark station ready
          </>}
      </button>
    </motion.div>;
}
// === Expediter ticket (all items grouped by station) ===
function ExpediterTicket({
  order,
  stations,
  onToggleItem,
  onMarkStationReady,
  onMarkAllReady,
  isCompleting
}: {
  order: Order;
  stations: KitchenStation[];
  onToggleItem: (itemId: string, ready: boolean) => void;
  onMarkStationReady: (stationId: string) => void;
  onMarkAllReady: () => void;
  isCompleting: boolean;
}) {
  const {
    getOutletsForBranch
  } = useOutlets();
  const branchOutlets = getOutletsForBranch(order.branchId || '');
  const outlet = order.outletId ? branchOutlets.find(o => o.id === order.outletId) : null;
  const {
    level
  } = urgencyOf(order.placedAt);
  const styles = urgencyStyle[level];
  // Group items by station
  const groups = useMemo(() => {
    const m: Record<string, OrderItem[]> = {};
    order.items.forEach(it => {
      const sid = stationForItem(it);
      if (!m[sid]) m[sid] = [];
      m[sid].push(it);
    });
    return m;
  }, [order.items]);
  // Order groups in same sequence as branch.stations, plus any unknown stations at the end
  const orderedStationIds: string[] = [];
  stations.forEach(s => {
    if (groups[s.id]) orderedStationIds.push(s.id);
  });
  Object.keys(groups).forEach(sid => {
    if (!orderedStationIds.includes(sid)) orderedStationIds.push(sid);
  });
  const totalItems = order.items.length;
  const readyItems = order.items.filter(i => i.ready).length;
  const allReady = readyItems === totalItems && totalItems > 0;
  const progressPct = totalItems > 0 ? readyItems / totalItems * 100 : 0;
  const stationById: Record<string, KitchenStation | undefined> = {};
  stations.forEach(s => stationById[s.id] = s);
  return <motion.div layout initial={{
    opacity: 0,
    y: 12,
    scale: 0.96
  }} animate={{
    opacity: 1,
    y: 0,
    scale: isCompleting ? 1.02 : 1
  }} exit={{
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2
    }
  }} transition={{
    duration: 0.25
  }} className={`relative bg-white rounded-xl border-2 overflow-hidden shadow-lg ${styles.border} ${level === 'overdue' ? 'animate-[pulse_2s_ease-in-out_infinite]' : ''}`} data-id="element-2367">
      <div className={`h-1.5 ${styles.bar}`} data-id="element-2368" />
      <TicketHeader order={order} data-id="element-2369" />

      {/* Station readiness chips */}
      <div className="px-4 pt-2 pb-2 flex items-center gap-1.5 flex-wrap border-b border-gray-100" data-id="element-2370">
        {orderedStationIds.map(sid => {
        const s = stationById[sid];
        const items = groups[sid] || [];
        const ready = items.filter(it => it.ready).length;
        const total = items.length;
        const done = ready === total && total > 0;
        return <span key={sid} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${done ? 'bg-emerald-100 text-emerald-700' : `${stationChipBg[s?.color || 'orange'] || 'bg-gray-100 text-gray-700'}`}`} data-id="element-2371">
              {done ? <Check size={9} strokeWidth={3} data-id="element-2372" /> : <span className={`w-1.5 h-1.5 rounded-full ${stationDotColor[s?.color || 'orange'] || 'bg-gray-400'}`} data-id="element-2373" />}
              {s?.name || sid}
              <span className="opacity-70 tabular-nums" data-id="element-2374">
                {ready}/{total}
              </span>
            </span>;
      })}
      </div>

      {order.method === 'delivery' && order.deliveryAddress && <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex gap-2 items-start" data-id="element-2375">
          <Bike size={13} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2376" />
          <p className="text-xs font-semibold text-blue-900 leading-snug" data-id="element-2377">
            {order.deliveryAddress}
          </p>
        </div>}
      {order.customerNotes && <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex gap-2 items-start" data-id="element-2378">
          <MessageSquare size={13} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2379" />
          <p className="text-xs font-semibold text-amber-900 leading-snug" data-id="element-2380">
            {order.customerNotes}
          </p>
        </div>}

      {/* Overall progress */}
      <div className="px-4 pt-3 pb-1" data-id="element-2381">
        <div className="flex items-center justify-between mb-1.5" data-id="element-2382">
          <span className={`text-[10px] font-bold tracking-wider uppercase ${allReady ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-2383">
            {readyItems} of {totalItems} ready
          </span>
          {allReady && <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-2384">
              READY TO PASS
            </span>}
        </div>
        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-2385">
          <motion.div className="h-full rounded-full bg-emerald-500" initial={false} animate={{
          width: `${progressPct}%`
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} data-id="element-2386" />
        </div>
      </div>

      {/* Items grouped by station */}
      <div className="px-2 py-2 space-y-3" data-id="element-2387">
        {orderedStationIds.map(sid => {
        const s = stationById[sid];
        const items = groups[sid] || [];
        const ready = items.filter(it => it.ready).length;
        const total = items.length;
        const done = ready === total && total > 0;
        return <div key={sid} className="rounded-lg bg-gray-50 border border-gray-100" data-id="element-2388">
              <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-gray-100" data-id="element-2389">
                <div className="flex items-center gap-1.5" data-id="element-2390">
                  {s ? <StationIconBadge icon={s.icon} color={s.color} size={10} data-id="element-2391" /> : <div className="w-4 h-4 rounded-md bg-gray-200" data-id="element-2392" />}
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-700" data-id="element-2393">
                    {s?.name || sid}
                  </span>
                </div>
                <button onClick={() => onMarkStationReady(sid)} disabled={done} className={`text-[10px] font-bold px-1.5 py-0.5 rounded transition-colors ${done ? 'bg-emerald-100 text-emerald-700 cursor-default' : 'bg-white border border-gray-200 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200'}`} data-id="element-2394">
                  {done ? '✓ Done' : 'Mark ready'}
                </button>
              </div>
              <div className="p-1 space-y-1" data-id="element-2395">
                {items.map(item => <ItemRow key={item.id} item={item} onToggle={ready => onToggleItem(item.id, ready)} data-id="element-2396" />)}
              </div>
            </div>;
      })}
      </div>

      <button onClick={onMarkAllReady} disabled={allReady} className={allReady ? 'w-full py-3.5 text-white font-bold text-sm border-t bg-emerald-600 border-emerald-700 cursor-default animate-pulse flex items-center justify-center gap-2' : 'w-full py-3.5 text-white font-bold text-sm border-t bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 border-emerald-600 transition-colors flex items-center justify-center gap-2'} data-id="element-2397">
        {allReady ? <>
            <Sparkles size={16} strokeWidth={3} data-id="element-2398" />
            Sending to ready queue...
          </> : <>
            <Check size={16} strokeWidth={3} data-id="element-2399" />
            Mark entire order ready
          </>}
      </button>
    </motion.div>;
}
function ReadyTicket({
  order
}: {
  order: Order;
}) {
  const MethodIcon = methodIcon(order.method);
  const mc = methodConfig[order.method];
  const itemCount = order.items.reduce((s, i) => s + i.quantity, 0);
  return <motion.div layout initial={{
    opacity: 0,
    y: -8
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3" data-id="element-2400">
      <div className="flex items-center justify-between gap-2 mb-1.5" data-id="element-2401">
        <span className="font-mono text-xs font-bold text-emerald-300" data-id="element-2402">
          {order.id}
        </span>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-200 border border-slate-700" data-id="element-2403">
          <MethodIcon size={10} data-id="element-2404" />
          {order.tableNumber ? `T${order.tableNumber}` : mc.short}
        </span>
      </div>
      <div className="text-sm font-semibold text-white truncate mb-1" data-id="element-2405">
        {order.customerName}
      </div>
      <div className="text-[11px] text-slate-400" data-id="element-2406">
        {itemCount} item{itemCount === 1 ? '' : 's'} · awaiting pickup
      </div>
    </motion.div>;
}
function EmptyState({
  viewMode,
  stationName
}: {
  viewMode: ViewMode;
  stationName?: string;
}) {
  return <div className="h-full min-h-[60vh] flex items-center justify-center text-center" data-id="element-2407">
      <div data-id="element-2408">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4" data-id="element-2409">
          <ChefHat size={28} className="text-slate-500" data-id="element-2410" />
        </div>
        <h3 className="text-lg font-bold text-white mb-1" data-id="element-2411">All caught up</h3>
        <p className="text-sm text-slate-400 max-w-xs mx-auto" data-id="element-2412">
          {viewMode === 'station' && stationName ? `Nothing for ${stationName} right now. Other stations may still have items in progress.` : 'No active tickets in the queue. Accepted orders from the cashier will appear here.'}
        </p>
      </div>
    </div>;
}